import { createBrowserRouter, Outlet, RouterProvider } from 'react-router-dom';
import { Layout } from '@/components/Layout';
import { AuthProvider } from '@/features/auth/AuthContext';
import { GuestOnly, RequireAuth, RequireRole } from '@/features/auth/guards';
import { AboutPage } from '@/pages/About';
import { AccessibilityPage } from '@/pages/Accessibility';
import { AccountPage } from '@/pages/Account';
import { AdminPage } from '@/pages/Admin';
import { BrowsePage } from '@/pages/Browse';
import { CheckoutPage } from '@/pages/Checkout';
import { CookiesPage } from '@/pages/Cookies';
import { DevForumsPage } from '@/pages/DevForums';
import { HelpPage } from '@/pages/Help';
import { PrivacyPage } from '@/pages/Privacy';
import { TermsPage } from '@/pages/Terms';
import { EpisodePage } from '@/pages/Episode';
import { ForgotPasswordPage } from '@/pages/ForgotPassword';
import { GamesPage } from '@/pages/Games';
import { HomePage } from '@/pages/Home';
import { LoginPage } from '@/pages/Login';
import { MyListPage } from '@/pages/MyList';
import { NotFoundPage } from '@/pages/NotFound';
import { PlansPage } from '@/pages/Plans';
import { NotificationsPage } from '@/pages/Notifications';
import { ProductPage } from '@/pages/Product';
import { ProfilesPage } from '@/pages/Profiles';
import { ResetPasswordPage } from '@/pages/ResetPassword';
import { SearchPage } from '@/pages/Search';
import { ShowPage } from '@/pages/Show';
import { SignupPage } from '@/pages/Signup';
import { StatusPage } from '@/pages/Status';
import { StorePage } from '@/pages/Store';
import { VerifyEmailPage } from '@/pages/VerifyEmail';

function RootProviders() {
  return (
    <AuthProvider>
      <Outlet />
    </AuthProvider>
  );
}

const router = createBrowserRouter([
  {
    element: <RootProviders />,
    children: [
      // Standalone status page — intentionally NOT linked anywhere in the UI.
      { path: '/status', element: <StatusPage /> },
      {
        element: <Layout />,
        children: [
          { path: '/', element: <HomePage /> },
          { path: '/browse', element: <BrowsePage /> },
          { path: '/dev', element: <DevForumsPage /> },
          { path: '/games', element: <GamesPage /> },
          { path: '/store', element: <StorePage /> },
          { path: '/store/:productSlug', element: <ProductPage /> },
          { path: '/search', element: <SearchPage /> },
          { path: '/plans', element: <PlansPage /> },
          { path: '/checkout', element: <CheckoutPage /> },
          { path: '/about', element: <AboutPage /> },
          { path: '/help', element: <HelpPage /> },
          { path: '/terms', element: <TermsPage /> },
          { path: '/privacy', element: <PrivacyPage /> },
          { path: '/cookies', element: <CookiesPage /> },
          { path: '/accessibility', element: <AccessibilityPage /> },
          { path: '/signup', element: <GuestOnly><SignupPage /></GuestOnly> },
          { path: '/login', element: <GuestOnly><LoginPage /></GuestOnly> },
          { path: '/forgot-password', element: <GuestOnly><ForgotPasswordPage /></GuestOnly> },
          { path: '/reset-password', element: <ResetPasswordPage /> },
          { path: '/verify-email', element: <VerifyEmailPage /> },
          { path: '/account', element: <RequireAuth><AccountPage /></RequireAuth> },
          { path: '/profiles', element: <RequireAuth><ProfilesPage /></RequireAuth> },
          { path: '/notifications', element: <RequireAuth><NotificationsPage /></RequireAuth> },
          { path: '/my-list', element: <RequireAuth><MyListPage /></RequireAuth> },
          {
            path: '/admin',
            element: (
              <RequireRole roles={['admin', 'owner', 'contributor']}>
                <AdminPage />
              </RequireRole>
            ),
          },
          { path: '/shows/:slug', element: <ShowPage /> },
          { path: '/shows/:slug/:episode', element: <EpisodePage /> },
          { path: '*', element: <NotFoundPage /> },
        ],
      },
    ],
  },
]);

export function App() {
  return <RouterProvider router={router} />;
}
